import { useAuth0 } from "@auth0/auth0-react";
import React, { createContext, useEffect, useMemo } from "react";

import { EnvVar } from "@/env";
import { useLogout } from "@/hooks/useLogout";

import { MaiteApiClient } from "../client/apiClient";

type ApiContextType = {
  apiClient: MaiteApiClient;
};

export const ApiContext = createContext<ApiContextType | null>(null);

interface ApiContextProviderProps {
  children: React.ReactNode;
}

export const ApiContextProvider: React.FC<ApiContextProviderProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();

  const apiClient = useMemo(() => {
    const client = new MaiteApiClient();

    const getAccessToken = async () => {
      try {
        const tokenResponse = await getAccessTokenSilently({
          cacheMode: "on",
          authorizationParams: {
            audience: EnvVar.auth0Audience,
            scope: "profile email read:current_user",
          },
          detailedResponse: true,
        });
        return tokenResponse.id_token || "";
      } catch (error) {
        console.error("Failed to get access token:", error);
        throw error;
      }
    };

    const handleUnauthorized = () => {
      console.warn("Unauthorized API call, logging out");
    };

    client.setupAuthInterceptor(getAccessToken, handleUnauthorized);
    return client;
  }, [getAccessTokenSilently]);

  const contextValue = useMemo(
    () => ({
      apiClient,
    }),
    [apiClient]
  );

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};
